// @ts-check
import axios from "axios";
import { FeatureFlagsService, IncidentsService, OpenAPI } from "./generated";
import { request as requestApi } from "./generated/core/request";
import { withApi } from "./openapiClient";

/**
 * @typedef {import("./generated").PaginatedResponse_IncidentRead_} PaginatedIncidentRead
 * @typedef {import("./generated").IncidentRead} IncidentRead
 * @typedef {import("./generated").IncidentCreate} IncidentCreate
 * @typedef {import("./generated").IncidentUpdate} IncidentUpdate
 * @typedef {import("./generated").IncidentStatusUpdate} IncidentStatusUpdate
 * @typedef {import("./generated").IncidentNoteCreate} IncidentNoteCreate
 * @typedef {import("./generated").IncidentNoteRead} IncidentNoteRead
 * @typedef {import("./generated").IncidentAttachmentCreate} IncidentAttachmentCreate
 * @typedef {import("./generated").IncidentAttachmentRead} IncidentAttachmentRead
 * @typedef {import("./generated").IncidentHistoryRead} IncidentHistoryRead
 * @typedef {import("./generated").IncidentSummary} IncidentSummary
 * @typedef {import("./generated").IncidentMetrics} IncidentMetrics
 * @typedef {import("./generated").IncidentTimeline} IncidentTimeline
 * @typedef {import("./generated").Body_upload_attachment_incidents__incident_id__attachments_post} IncidentAttachmentUploadBody
 */

/**
 * @typedef {Object} IncidentListParams
 * @property {string} [status]
 * @property {string} [severity]
 * @property {string} [severity_values]
 * @property {string} [priority]
 * @property {number} [limit]
 * @property {number} [offset]
 * @property {string} [sort]
 * @property {string} [q]
 * @property {string} [owner_id]
 * @property {boolean} [breached]
 * @property {boolean} [escalated]
 * @property {boolean} [needs_attention]
 * @property {boolean} [open_only]
 * @property {string} [created_from]
 * @property {string} [created_to]
 */

const blobClient = axios.create({ responseType: "blob" });

/**
 * @param {IncidentListParams} [params]
 * @returns {Promise<{ data: PaginatedIncidentRead }>}
 */
export const getIncidents = (params = {}) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents",
      query: {
        status: params.status,
        severity: params.severity,
        severity_values: params.severity_values,
        priority: params.priority,
        limit: params.limit,
        offset: params.offset,
        sort: params.sort,
        q: params.q,
        owner_id: params.owner_id,
        breached: params.breached,
        escalated: params.escalated,
        needs_attention: params.needs_attention,
        open_only: params.open_only,
        created_from: params.created_from,
        created_to: params.created_to,
      },
    })
  );

/**
 * @param {string | number} id
 * @returns {Promise<{ data: IncidentRead }>}
 */
export const getIncidentById = (id) =>
  withApi(
    IncidentsService.getIncidentIncidentsIncidentIdGet({
      incidentId: String(id),
    })
  );

/**
 * @param {IncidentCreate} data
 * @returns {Promise<{ data: IncidentRead }>}
 */
export const createIncident = (data) =>
  withApi(
    IncidentsService.createIncidentIncidentsPost({
      requestBody: data,
    })
  );

/**
 * @param {string | number} id
 * @param {IncidentUpdate} data
 * @returns {Promise<{ data: IncidentRead }>}
 */
export const updateIncident = (id, data) =>
  withApi(
    IncidentsService.updateIncidentIncidentsIncidentIdPut({
      incidentId: String(id),
      requestBody: data,
    })
  );

/**
 * @param {string | number} id
 * @param {IncidentStatusUpdate} statusPayload
 * @returns {Promise<{ data: IncidentRead }>}
 */
export const updateIncidentStatus = (id, statusPayload) =>
  withApi(
    IncidentsService.updateIncidentStatusIncidentsIncidentIdStatusPatch({
      incidentId: String(id),
      requestBody: statusPayload,
    })
  );

/**
 * @param {string | number} id
 * @param {{ resolution_summary: string, updated_by?: string | null }} payload
 * @returns {Promise<{ data: IncidentRead }>}
 */
export const closeIncident = (id, payload) =>
  withApi(
    IncidentsService.closeIncidentIncidentsIncidentIdClosePost({
      incidentId: String(id),
      requestBody: payload,
    })
  );

/**
 * @param {string | number} id
 * @param {{ reopen_reason: string, updated_by?: string | null }} payload
 * @returns {Promise<{ data: IncidentRead }>}
 */
export const reopenIncident = (id, payload) =>
  withApi(
    IncidentsService.reopenIncidentIncidentsIncidentIdReopenPost({
      incidentId: String(id),
      requestBody: payload,
    })
  );

/**
 * @param {string | number} id
 * @param {IncidentNoteCreate} data
 * @returns {Promise<{ data: IncidentNoteRead }>}
 */
export const addIncidentNote = (id, data) =>
  withApi(
    IncidentsService.addNoteIncidentsIncidentIdNotesPost({
      incidentId: String(id),
      requestBody: data,
    })
  );

/**
 * @param {string | number} id
 * @param {FormData | IncidentAttachmentUploadBody | { file?: Blob }} data
 * @returns {Promise<{ data: IncidentAttachmentRead }>}
 */
export const addIncidentAttachment = (id, data) => {
  const formData =
    data instanceof FormData
      ? data
      : (() => {
          const fd = new FormData();
          if (data?.file) {
            fd.append("file", data.file);
          }
          return fd;
        })();
  return withApi(
    IncidentsService.uploadAttachmentIncidentsIncidentIdAttachmentsPost({
      incidentId: String(id),
      formData,
    })
  );
};

/**
 * @param {string | number} id
 * @param {IncidentAttachmentCreate} payload
 * @returns {Promise<{ data: IncidentAttachmentRead }>}
 */
export const addIncidentAttachmentUrl = (id, payload) =>
  withApi(
    IncidentsService.addAttachmentUrlIncidentsIncidentIdAttachmentsUrlPost({
      incidentId: String(id),
      requestBody: payload,
    })
  );

/**
 * @param {string | number} id
 * @returns {Promise<{ data: IncidentHistoryRead[] }>}
 */
export const getIncidentHistory = (id) =>
  withApi(
    IncidentsService.listHistoryIncidentsIncidentIdHistoryGet({
      incidentId: String(id),
    })
  );

/**
 * @param {string | number} id
 * @returns {Promise<{ data: IncidentNoteRead[] }>}
 */
export const getIncidentNotes = (id) =>
  withApi(
    IncidentsService.listNotesIncidentsIncidentIdNotesGet({
      incidentId: String(id),
    })
  );

/**
 * @param {string | number} id
 * @returns {Promise<{ data: IncidentAttachmentRead[] }>}
 */
export const getIncidentAttachments = (id) =>
  withApi(
    IncidentsService.listAttachmentsIncidentsIncidentIdAttachmentsGet({
      incidentId: String(id),
    })
  );

export const updateIncidentAttachmentMetadata = (incidentId, attachmentId, payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "PATCH",
      url: "/incidents/{incident_id}/attachments/{attachment_id}",
      path: {
        incident_id: String(incidentId),
        attachment_id: Number(attachmentId),
      },
      body: payload,
      mediaType: "application/json",
    })
  );

export const getIncidentRca = (incidentId) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/{incident_id}/rca",
      path: {
        incident_id: String(incidentId),
      },
    })
  );

export const createIncidentRca = (incidentId, payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "POST",
      url: "/incidents/{incident_id}/rca",
      path: {
        incident_id: String(incidentId),
      },
      body: payload,
      mediaType: "application/json",
    })
  );

export const updateIncidentRca = (incidentId, rcaId, payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "PUT",
      url: "/incidents/{incident_id}/rca/{rca_id}",
      path: {
        incident_id: String(incidentId),
        rca_id: Number(rcaId),
      },
      body: payload,
      mediaType: "application/json",
    })
  );

export const deleteIncidentRca = (incidentId, rcaId) =>
  withApi(
    requestApi(OpenAPI, {
      method: "DELETE",
      url: "/incidents/{incident_id}/rca/{rca_id}",
      path: {
        incident_id: String(incidentId),
        rca_id: Number(rcaId),
      },
    })
  );

export const getIncidentActionLinks = (incidentId) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/{incident_id}/actions",
      path: {
        incident_id: String(incidentId),
      },
    })
  );

export const upsertIncidentActionLink = (incidentId, actionId, payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "PUT",
      url: "/incidents/{incident_id}/actions/{action_id}",
      path: {
        incident_id: String(incidentId),
        action_id: Number(actionId),
      },
      body: payload,
      mediaType: "application/json",
    })
  );

/**
 * @param {string | number} id
 * @param {number} [level]
 * @returns {Promise<{ data: IncidentRead }>}
 */
export const escalateIncident = (id, level) =>
  withApi(
    IncidentsService.escalateIncidentsIncidentIdEscalatePost({
      incidentId: String(id),
      level: level ?? undefined,
    })
  );

/**
 * @returns {Promise<{ data: IncidentSummary }>}
 */
export const getIncidentSummary = () => withApi(IncidentsService.incidentSummaryIncidentsSummaryGet());

/**
 * @returns {Promise<{ data: IncidentMetrics }>}
 */
export const getIncidentMetrics = () => withApi(IncidentsService.incidentMetricsIncidentsMetricsGet());

/**
 * @param {string} key
 * @returns {Promise<{ data: unknown }>}
 */
export const getFeatureFlag = (key) =>
  withApi(
    FeatureFlagsService.getFeatureFlagFeatureFlagsFeatureKeyGet({
      featureKey: key,
    })
  );

/**
 * @returns {Promise<{ data: Record<string, unknown> }>}
 */
export const getEscalationRules = () => withApi(IncidentsService.escalationRulesIncidentsEscalationRulesGet());

/**
 * @param {string | number} id
 * @returns {Promise<{ data: IncidentTimeline }>}
 */
export const getIncidentTimeline = (id) =>
  withApi(
    IncidentsService.incidentTimelineIncidentsIncidentIdTimelineGet({
      incidentId: String(id),
    })
  );

export const getIncidentReportingOverview = (params = {}) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/reporting/overview",
      query: {
        date_from: params.date_from,
        date_to: params.date_to,
        status: params.status,
        severity: params.severity,
        severity_values: params.severity_values,
        priority: params.priority,
        owner_id: params.owner_id,
        breached_only: params.breached_only,
        escalated: params.escalated,
        open_only: params.open_only,
      },
    })
  );

export const getIncidentReportingTrends = (params = {}) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/reporting/trends",
      query: {
        date_from: params.date_from,
        date_to: params.date_to,
        status: params.status,
        severity: params.severity,
        severity_values: params.severity_values,
        priority: params.priority,
        owner_id: params.owner_id,
        breached_only: params.breached_only,
        escalated: params.escalated,
        open_only: params.open_only,
      },
    })
  );

export const getIncidentReportingWorkload = (params = {}) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/reporting/workload",
      query: {
        date_from: params.date_from,
        date_to: params.date_to,
        status: params.status,
        severity: params.severity,
        severity_values: params.severity_values,
        priority: params.priority,
        owner_id: params.owner_id,
        breached_only: params.breached_only,
        escalated: params.escalated,
        open_only: params.open_only,
      },
    })
  );

export const getIncidentSavedViews = () =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/saved-views",
    })
  );

export const createIncidentSavedView = (payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "POST",
      url: "/incidents/saved-views",
      body: payload,
      mediaType: "application/json",
    })
  );

export const updateIncidentSavedView = (viewId, payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "PUT",
      url: "/incidents/saved-views/{view_id}",
      path: { view_id: Number(viewId) },
      body: payload,
      mediaType: "application/json",
    })
  );

export const deleteIncidentSavedView = (viewId) =>
  withApi(
    requestApi(OpenAPI, {
      method: "DELETE",
      url: "/incidents/saved-views/{view_id}",
      path: { view_id: Number(viewId) },
    })
  );

export const getIncidentSchedulerRuns = (limit = 20) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/ops/scheduler-runs",
      query: { limit },
    })
  );

export const getIncidentNotificationEvents = (limit = 20) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/ops/notification-events",
      query: { limit },
    })
  );

export const getIncidentAdminTaxonomy = (dimension) =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/admin/taxonomy/{dimension}",
      path: { dimension },
    })
  );

export const updateIncidentAdminTaxonomy = (dimension, payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "PUT",
      url: "/incidents/admin/taxonomy/{dimension}",
      path: { dimension },
      body: payload,
      mediaType: "application/json",
    })
  );

export const getIncidentWorkflowConfig = () =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/admin/workflow",
    })
  );

export const updateIncidentWorkflowConfig = (payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "PUT",
      url: "/incidents/admin/workflow",
      body: payload,
      mediaType: "application/json",
    })
  );

export const getIncidentNotificationRoutingConfig = () =>
  withApi(
    requestApi(OpenAPI, {
      method: "GET",
      url: "/incidents/admin/notification-routing",
    })
  );

export const updateIncidentNotificationRoutingConfig = (payload) =>
  withApi(
    requestApi(OpenAPI, {
      method: "PUT",
      url: "/incidents/admin/notification-routing",
      body: payload,
      mediaType: "application/json",
    })
  );

/**
 * @param {number | string} attachmentId
 * @returns {Promise<{ data: Blob }>}
 */
export const downloadAttachment = (attachmentId) =>
  withApi(
    requestApi(
      OpenAPI,
      {
        method: "GET",
        url: "/attachments/{attachment_id}/download",
        path: {
          attachment_id: attachmentId,
        },
      },
      blobClient
    )
  );
